import { useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import { ArrowRight, Copy, Download } from 'lucide-react'
import BetaFooter from './BetaFooter'
import HeroCarousel from './HeroCarousel'
import Certificate, { cleanName, downloadBlob } from './Certificate'
import type { CertificateHandle } from './Certificate'
import { founderRoles, founderUrl, getFounderRole } from './founderRoles'
import './sections.css'

// Doodle icons drawn by Codex, one per role.
const roleIcons: Record<string, string> = { visionary: 'rocket', stealth: 'sunglasses', 'thought-leader': 'brain', 'almost-founder': 'loop' }

const preview = import.meta.env.DEV || import.meta.env.VITE_LARPUP_PREVIEW === 'true'

type Metric = 'role_selected' | 'card_downloaded' | 'share_intent_opened' | 'link_copied'
// Local-only instrumentation hooks, not an analytics collector. Never include email.
function signal(event: Metric, role: string) {
  window.dispatchEvent(new CustomEvent('larpup:metric', { detail: { event, role } }))
}

export default function App() {
  const [role, setRole] = useState(() => getFounderRole(new URLSearchParams(window.location.search).get('role')))
  const [cardMessage, setCardMessage] = useState('')
  const [downloading, setDownloading] = useState(false)
  const [showCopy, setShowCopy] = useState(false)
  const [certified, setCertified] = useState(false)
  const [name, setName] = useState('')
  const certRef = useRef<CertificateHandle>(null)
  const shareUrl = founderUrl(role)
  const xIntent = `https://twitter.com/intent/tweet?text=${encodeURIComponent(role.share)}&url=${encodeURIComponent(founderUrl(role, 'x'))}`

  async function downloadCard() {
    if (!certRef.current || downloading) return
    setDownloading(true)
    setCardMessage('')
    try {
      const blob = await certRef.current.toBlob()
      downloadBlob(blob, `larpup-certificate-${role.id}.png`)
      setCardMessage('Certificate ready. Attach it to your post; the X button prepares the text and link.')
      setCertified(true)
      signal('card_downloaded', role.id)
    } catch { setCardMessage('The image could not be saved. You can still copy the link or take a screenshot.') }
    finally { setDownloading(false) }
  }

  async function copyLink() {
    try {
      if (!navigator.clipboard) throw new Error('Clipboard unavailable')
      await navigator.clipboard.writeText(shareUrl)
      setCardMessage('Link copied. Send it to your future co-founder.')
      signal('link_copied', role.id)
    } catch {
      setShowCopy(true)
      setCardMessage('Select and copy the link below.')
    }
  }

  return (
    <>
      {preview && <div className="preview-banner">PRIVATE PREVIEW · Form submissions are disabled. Nothing is saved.</div>}
      <a className="skip-link" href="#main">Skip to content</a>
      <a className="beta-bar" href="#beta"><span><strong>NEW:</strong> LARPup is becoming a game. Steam beta seats are open.</span><ArrowRight size={14} /></a>
      <main id="main">
        <HeroCarousel />
        <section className="pick" id="make-card" aria-labelledby="pick-title">
          <span className="sx-label">Step 1 · choose your delusion</span>
          <div className="pick-giant" aria-hidden="true">PICK ONE</div>
          <h2 id="pick-title">Pick your title.</h2>
          <div className="pick-fan" role="group" aria-label="Pick your founder persona">
            {founderRoles.map((item, i) => (
              <button key={item.id} type="button" className="pick-card" aria-pressed={item.id === role.id}
                style={{ '--r': `${(i - 1.5) * 6}deg`, '--y': `${Math.abs(i - 1.5) * 16}px` } as CSSProperties}
                onClick={() => { setRole(item); setCardMessage(''); setShowCopy(false); signal('role_selected', item.id) }}>
                <img src={`/sections/icon-${roleIcons[item.id]}.png`} alt="" />
                <b>{item.label}</b>
                <span>{item.quote}</span>
                {item.id === role.id && <em>Your pick ✓</em>}
              </button>
            ))}
          </div>
          <p className="pick-hint">Your card updates below ↓</p>
        </section>
        <section className="proof" aria-labelledby="proof-title">
          <div className="proof-copy">
            <span className="sx-label">Step 2 · the evidence</span>
            <span className="maxx">LarpMaxxing</span>
            <h2 id="proof-title"><span>Your</span> <span>certificate.</span> <span>Issued.</span></h2>
          </div>
          <div className="proof-card">
            <Certificate ref={certRef} role={role} name={name} />
          </div>
          <div className="proof-side">
            <label className="name-field">
              <span>Your name on the certificate</span>
              <input value={name} onChange={e => setName(cleanName(e.target.value))} onBlur={e => setName(e.target.value.trim())} maxLength={24} placeholder="Type your name" autoComplete="name" spellCheck={false} />
              <small>Stays on your device. Never saved, never in the share link.</small>
            </label>
            <p><strong>Download it. Post it.</strong> Let your group chat panic.</p>
            <div className="card-actions">
              <button type="button" className="download" onClick={downloadCard} disabled={downloading}><Download size={16} />{downloading ? 'Printing…' : 'Download certificate'}</button>
              <a href={xIntent} target="_blank" rel="noopener noreferrer" onClick={() => signal('share_intent_opened', role.id)}>Share on X <ArrowRight size={15} /></a>
              <button type="button" className="copy-button" onClick={copyLink} aria-label="Copy your role link"><Copy size={16} /> Copy link</button>
            </div>
            <p className="card-message" role="status">{cardMessage}</p>
            {certified && <a className="certified-link" href="#beta">You're certified. Claim your Steam beta seat ↓</a>}
            {showCopy && <input className="copy-fallback" aria-label="Share link" readOnly value={shareUrl} onFocus={e => e.target.select()} />}
            <p className="proof-note">Free. No signup needed. The card is yours.</p>
            <p className="proof-fine">* Max out your larping skills. Not a real skill. Certificate valid for nothing.</p>
          </div>
        </section>
      </main>
      <BetaFooter role={role} preview={preview} />
    </>
  )
}
